import { VideoAdserverSettings, VideoPositionSettings } from '@mbrtargeting/metatag-config-types';
import { generateRandomNumberString, getBrowserDetails, isBoolean, isString } from '@mbrtargeting/metatag-utils';
import { inject, injectionTarget } from '../../../loader/decorators/inject.js';
import { ConfigResolver } from '../../../loader/essentials/config-resolver.js';
import { selectFirstActiveVideoAdserver, selectVideoPositions } from '../../../loader/essentials/config-selector.js';
import { logger } from '../../../loader/essentials/logger.js';
import { ConsentCache } from '../../../loader/modules/consent-cache.js';
import { CONFIG_RESOLVER, CONSENT_CACHE } from '../../../loader/token.js';
import { renderTemplate } from '../../../utils/render-template.js';
import { GLOBAL_SETTINGS, GLOBAL_TARGETING, ID_STORE } from '../../token.js';
import { constructAdUnitPath } from '../../utils/construct-adunit-path.js';
import { GlobalSettings } from '../global-settings/global-settings.js';
import { GlobalTargeting } from '../global-targeting/global-targeting.js';
import { IdStore } from '../idstore/idstore.js';

const log = logger.logGroup({ prefix: 'VIDEO' });

type VideoPositionsPerZone = Record<string, VideoPositionSettings>;

@injectionTarget()
export class VastTagGenerator {

    @inject(CONFIG_RESOLVER) private configResolver!: ConfigResolver;
    @inject(GLOBAL_SETTINGS) private globalSettings!: GlobalSettings;
    @inject(GLOBAL_TARGETING) private globalTargeting!: GlobalTargeting;
    @inject(ID_STORE) private idStore!: IdStore;
    @inject(CONSENT_CACHE) private consentCache!: ConsentCache;

    private getVideoPositionsPerZone(): VideoPositionsPerZone {
        const { isPhone } = getBrowserDetails();
        return this.configResolver.get<VideoPositionsPerZone>(selectVideoPositions(isPhone ? 'mew' : 'desktop')) || {};
    }

    /**
     * will check if the zone, requested by the publisher, exists in the config.
     * Some publisher have a lot of "subZones", which they will use in the initial call for VAST URL.
     * In most cases this subZones are redundant and the VAST tags of the parent Zone has to be used.
     *
     * Should we not find the requested zone to be a "parent" zone in our config, we crawl all configured subZones, in hope to find the requested zone somewhere.
     * If we find the subZone, determine which parent Zone to use and report the correct zone back
     *
     * @param videoZone
     */
    private checkConfigForVideoZone(videoZone: string): string {
        const videoPositionsPerZone = this.getVideoPositionsPerZone();
        // if the videoZone exists directly in config, just return it
        if (Object.keys(videoPositionsPerZone).includes(videoZone)) return videoZone;
        // search in possibleCoZones
        for (const [zone, settings] of Object.entries(videoPositionsPerZone)) {
            const coZone = settings?.possibleCoZones?.find(entry => entry === videoZone);
            if (coZone) {
                log.debug('Co-Zone detected: "%s"', [coZone]);
                return zone;
            }
        }
        // no found at all, use 'rest' as videoZone
        log.warn('The zone "%s" you provided was not found in our config. Defaulting to "rest" zone.', [videoZone]);
        return 'rest';
    };

    /**
     * Returns the specific adTag-ID of the given Slot for the current zone.
     *
     * @param slotName the slotName
     * @param videoZone the video zone
     * @returns the adTagID
     */
    private getTagId(slotName: string, videoZone: string): string | undefined {
        const videoPositionsPerZone: VideoPositionsPerZone = this.getVideoPositionsPerZone();
        const videoPositions = videoPositionsPerZone[videoZone];

        if (!videoPositions) {
            log.warn('The zone used to generate VAST URLs "%s" is not found in the video configuration. Video AdTags will likely be incorrect. Please contact your Ströer representative', [videoZone]);
            return;
        }

        const value = (<any>videoPositions)[slotName];
        if (isString(value) && value !== '') {
            return value;
        }

        log.warn('The format "%s" you provided was not found in our configuration. Video AdTag for this format can not be provided. Please contact your Ströer representative', [slotName]);
    }

    /**
     * Creates a video tag, based on the given Template in config. The parameters for rendering each tag are generated individually.
     *
     * @param slotName The kind of id we are going to load. could be 'preRollId' or 'postRollId', for example.
     * @param videoZone the video zone
     * @param adserverConfig config for the given video adServer
     * @returns the actual vast-tag with the chosen id, rendered via tag template defined in config
     */
    private createVideoTag(slotName: string, videoZone: string, adserverConfig: VideoAdserverSettings['config']): string | undefined {

        const tagID = this.getTagId(slotName, videoZone);

        if (tagID) {
            const tcData = this.consentCache.getTcData();
            const { tagTemplate = '', allowedKeyValues = [] } = adserverConfig;
            const { isPhone } = getBrowserDetails();

            //video positions dont have separate "position" entries in config, use stand-ins
            const fullAdUnitPath = constructAdUnitPath({ slotName: isPhone ? 'topmobile' : 'banner' }, { addMcmId: false, addDfpZonePostfix: false });

            //break down adUnit into individual parts, for using as placeholders
            const [networkId = '', adUnit1 = '', adUnit2 = '', adUnit3 = '', adUnit4 = '', adUnit5] = fullAdUnitPath.split('/').filter(value => value !== ''); //remove leading slash/

            //have id solutions as one "whole" and separate parts for placeholders
            const { id5id = '', criteo = '', sharedid = '', utiq: atid = '', utiq_mtid: mtid = '' } = this.idStore.getIds();
            const id5linktype = this.idStore.getIdData('id5id')?.attr?.linkType;
            const ppid = sharedid.replaceAll(/[\W_]/g, '');
            const targeting = {
                id5id: [id5id.replaceAll('*', '€')], // replace all Asterisks with Euro signs because they are disallowed by GAM
                criteo: [criteo.replaceAll('*', '€')], // replace all Asterisks with Euro signs because they are disallowed by GAM
                sharedid: [sharedid.replaceAll('*', '€')], // replace all Asterisks with Euro signs because they are disallowed by GAM
                pubid: [ppid],
                ...this.globalTargeting.filter(([key]) => allowedKeyValues.includes(key)).toObject()
            };
            log.debug('VAST-Tag created: zone : %o | slotName : %o | adTagId : %o ', [videoZone, slotName, tagID]);

            return renderTemplate(tagTemplate, {
                protocol: 'https:',
                tagID,
                duration: (videoZone === 'livestream') ? '60000' : '30000',
                device: (isPhone) ? 'm.mew' : 'desktop',
                keyValues: Object.entries(targeting)
                    .map(([key, values]) => `${key}%3D${values.map(value => encodeURIComponent(value)).join('%2C')}`)
                    .join('%26'),
                referrer: encodeURIComponent(document.location.href.slice(0, 250)),
                misc: generateRandomNumberString(10),
                gdpr: tcData?.gdprApplies ? 1 : 0,
                gdpr_consent: tcData?.tcString || '',
                networkId,
                adUnit1,
                adUnit2,
                adUnit3,
                adUnit4,
                adUnit5,
                videoZone,
                slotName,
                vpos: adserverConfig.vpos?.[slotName] ?? 'preroll', // defaults to preroll if slotname has no valid vpos
                id5id,
                id5linktype: id5linktype ? `--linktype${id5linktype}` : '',
                criteo,
                sharedid,
                atid,
                mtid,
                ppid,
                teamName: this.globalTargeting.get('team').first() ?? '', //sport1 workaround for future keyValues selector in vast tag
            });
        }
    }

    /**
     * parse the publisher input and generate VAST Urls than output all tags to the publisher
     *
     * @param config
     * @param videoZone the video zone
     * @returns vast urls per vast ad type
     */
    private buildOutputForPublisher(config: Record<string, boolean>, videoZone: string): Record<string, string> {
        videoZone = this.checkConfigForVideoZone(videoZone);

        const videoConfig = this.configResolver.get(selectFirstActiveVideoAdserver())?.config ?? {}

        return Object.entries(config)
            .filter(([/*realSlotName*/, enabled]) => enabled === true)
            .reduce<Record<string, string>>((result, [realSlotName]) => {
                const alias: string | undefined = (<any>videoConfig)[realSlotName];
                const slotNameForPublisher = isString(alias) && alias.length > 0 ? alias : realSlotName;
                const vastUrl = this.createVideoTag(realSlotName, videoZone, videoConfig);
                if (vastUrl) {
                    result[slotNameForPublisher] = vastUrl;
                }
                return result;
            }, {});
    }

    /**
     * Method will check what the publisher entered into the generateVastUrls method.
     * It was extensively redesigned and used 3 different approaches how the publisher could enter data.
     * Therefor the method will try to guess what the intent of the publisher is and construct an object we can further process.
     *
     * @param configOrString comma-delimited string, may contain 'prer,prepres,midr,postr,postpres,over' OR predefined config object
     * @param zone optional, backwards compatible (Publisher should not use anymore, dont mention in documentation. the zone setting used by the publisher when he wishes to use another zone, different from the display ad zone.
     * @returns vast urls per vast ad type
     */
    public generateVastUrls(configOrString: string | Record<string, unknown>, zone?: string | undefined): Record<string, string> {
        const getFallbackZone = (): string => {
            const zone = this.globalSettings.getZone();
            return zone !== 'zoneError' ? zone : 'rest';
        };

        if (!configOrString) {
            log.warn('No parameters provided for generateVastUrls()!');
            return {};
        }

        if (isString(configOrString)) {
            const mapping: Record<string, string> = {
                'prer': 'preroll',
                'midr': 'midroll',
                'postr': 'postroll',
                'prepres': 'presenterpre',
                'postpres': 'presenterpost',
                'over': 'overlay',
            };
            const config = configOrString.replace(/ /g, '').split(',').reduce<Record<string, boolean>>((result, key) => {
                result[mapping[key] ?? key] = true;
                return result;
            }, {});
            return this.buildOutputForPublisher(config, zone ?? getFallbackZone());
        }

        const videozone = isString(configOrString.videozone) ? configOrString.videozone : '';
        const config = Object.entries(configOrString).reduce<Record<string, boolean>>((result, [key, value]) => {
            if (isBoolean(value)) result[key] = value;
            return result;
        }, {});
        return this.buildOutputForPublisher(config, videozone ?? getFallbackZone());
    }
}
